/**
 * Waktu selalu ditampilkan dalam ZONA WAKTU TENANT, tidak pernah zona peramban.
 *
 * Ini hal kedua yang mahal dipasang belakangan, sesudah bahasa — dan lebih
 * berbahaya. Kalau tanggal diformat dengan zona peramban, meeting Jakarta pukul
 * 10:00 yang dibuka dari laptop ber-zona UTC tampil 03:00. Tidak ada galat, tidak
 * ada yang aneh di layar, dan orangnya datang salah jam.
 *
 * `tenants.timezone` diisi `GET /dashboard/tenant` lewat `<PenyediaZona>` sekali
 * sesudah masuk. Sampai jawabannya tiba — dan kalau ia tidak pernah tiba — yang
 * dipakai `BAWAAN` di bawah.
 *
 * BAWAANNYA BUKAN ZONA PERAMBAN. Peramban yang salah zona akan menampilkan jam yang
 * salah dengan yakin; 'Asia/Jakarta' setidaknya benar untuk setiap tenant yang ada
 * sekarang, dan salahnya bisa ditebak arahnya.
 *
 * Seluruh format lewat `Intl`, bukan pustaka tanggal: dua fungsi di bawah adalah
 * seluruh kebutuhan proyek ini, dan `Intl` sudah ada di tiap peramban.
 */

const BAWAAN = 'Asia/Jakarta'

let zona = BAWAAN
const pendengar = new Set()

/**
 * Apakah `Intl` mengenali zona ini?
 *
 * WAJIB diperiksa sebelum dipakai. `timeZone` yang tidak dikenal membuat
 * `Intl.DateTimeFormat` melempar `RangeError` — bukan menampilkan jam yang salah,
 * melainkan MENJATUHKAN setiap layar yang memformat tanggal. Nilainya datang dari
 * kolom `String(64)` yang diisi orang lewat formulir, jadi "Jakarta", "WIB", atau
 * string kosong adalah isi yang harus diduga, bukan dikecualikan.
 */
export function zonaSah(tz) {
  if (typeof tz !== 'string' || !tz.trim()) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/**
 * Pasang zona tenant. Yang tidak sah JATUH KE BAWAAN, tidak dilempar ke atas.
 *
 * Mengembalikan zona yang benar-benar dipakai, supaya pemanggil bisa tahu nilainya
 * ditolak tanpa harus memeriksanya lagi sendiri.
 */
export function setZonaTenant(tz) {
  const dipakai = zonaSah(tz) ? tz : BAWAAN
  if (dipakai === zona) return dipakai
  zona = dipakai
  // SALINAN, bukan set aslinya — dan alasannya BUKAN pendengar yang melepas dirinya
  // sendiri: `Set` sudah menangani penghapusan saat diiterasi tanpa melewati entri
  // berikutnya (dibuktikan kontrol negatif — mengiterasi set aslinya tetap lulus).
  // Yang TIDAK ditangani `Set` adalah PENAMBAHAN: entri yang masuk saat iterasi
  // berjalan ikut dikunjungi di putaran yang sama, jadi pendengar yang baru dipasang
  // menerima kabar perubahan yang sudah terjadi sebelum ia ada.
  for (const f of [...pendengar]) {
    try { f(dipakai) } catch { /* satu pendengar rusak tidak boleh menjatuhkan sisanya */ }
  }
  return dipakai
}

export function zonaTenant() {
  return zona
}

/**
 * Berlangganan perubahan zona. Mengembalikan fungsi pelepas.
 *
 * ADA KARENA `zonaTenant()` BUKAN STATE REACT. Keempat formatter di bawah fungsi
 * murni yang dipanggil saat render — tidak ada komponen yang berlangganan padanya.
 * Tanpa ini, zona yang tiba SESUDAH gambar pertama tidak pernah sampai ke tanggal
 * yang sudah terlanjur dirender: layarnya tetap memperlihatkan jam bawaan sampai ada
 * hal lain yang kebetulan memicu render ulang. `<PenyediaZona>` memasang satu
 * pendengar di atas seluruh pohon, jadi satu perubahan menggambar ulang semuanya.
 */
export function dengarZona(fn) {
  pendengar.add(fn)
  return () => pendengar.delete(fn)
}

function keTanggal(nilai) {
  if (nilai instanceof Date) return nilai
  if (typeof nilai === 'number') return new Date(nilai)
  if (typeof nilai === 'string' && nilai) {
    const d = new Date(nilai)
    return Number.isNaN(d.getTime()) ? null : d
  }
  return null
}

/** "9 Sep 2026" — tanggal saja, zona tenant. */
export function tanggal(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: zonaTenant(),
  }).format(d)
}

/** "10:00" — jam saja, zona tenant. */
export function jam(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale, {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: zonaTenant(),
  }).format(d)
}

/** "9 Sep 2026 · 10:00" */
export function tanggalJam(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  return `${tanggal(d, locale)} · ${jam(d, locale)}`
}

/**
 * "2 jam lalu" — untuk hal yang baru terjadi.
 *
 * Dipakai HANYA untuk yang lebih muda dari sehari. Lebih tua dari itu, "3 hari lalu"
 * kurang berguna daripada tanggalnya: orang yang menelusuri percakapan lama butuh
 * tanggal untuk dicocokkan dengan catatan lain.
 */
export function sejak(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  const detik = Math.round((Date.now() - d.getTime()) / 1000)
  if (detik < 0) return tanggalJam(d, locale)          // di masa depan
  if (detik >= 86400) return tanggalJam(d, locale)     // lebih tua dari sehari

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  if (detik < 60) return rtf.format(-detik, 'second')
  if (detik < 3600) return rtf.format(-Math.round(detik / 60), 'minute')
  return rtf.format(-Math.round(detik / 3600), 'hour')
}

/** "Sen, 5 Okt" — hari dan tanggal ringkas tanpa tahun, zona tenant. */
export function hariTanggalPendek(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: zonaTenant(),
  }).format(d)
}

/** "Jumat, 25 Des 2026" — hari, tanggal, dan tahun, zona tenant. Untuk daftar yang bisa
 *  menyeberang tahun, seperti tanggal libur. */
export function hariTanggalTahun(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long', day: 'numeric', month: 'short', year: 'numeric', timeZone: zonaTenant(),
  }).format(d)
}

/** "Sabtu, 3 Oktober" — hari dan tanggal tanpa tahun, zona tenant. */
export function hariTanggal(nilai, locale = 'id-ID') {
  const d = keTanggal(nilai)
  if (!d) return '—'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: zonaTenant(),
  }).format(d)
}

/**
 * Hari sebuah waktu di zona tenant, "2026-10-03". Untuk membandingkan, bukan ditampilkan:
 * dua waktu ada di hari yang sama kalau kuncinya sama.
 */
export function kunciHari(nilai) {
  const d = keTanggal(nilai)
  if (!d) return null
  // `en-CA` menulis tahun-bulan-hari.
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric', month: '2-digit', day: '2-digit', timeZone: zonaTenant(),
  }).format(d)
}

/** Apakah `nilai` jatuh pada hari ini di zona tenant. */
export function hariIni(nilai, kini = new Date()) {
  const k = kunciHari(nilai)
  return k !== null && k === kunciHari(kini)
}

/**
 * Singkatan zona tenant: "WIB", "WITA", "WIT". Zona tanpa singkatan ditulis selisihnya,
 * "GMT+8". Jam yang ditulis tanpa zona dibaca orang sebagai jamnya sendiri.
 */
export function labelZona(locale = 'id-ID', kini = new Date()) {
  const bagian = new Intl.DateTimeFormat(locale, { timeZone: zonaTenant(), timeZoneName: 'short' })
    .formatToParts(kini)
  return bagian.find((b) => b.type === 'timeZoneName')?.value ?? zonaTenant()
}

const SATUAN_RINGKAS_ID = { minute: 'mnt', hour: 'jam', day: 'hari' }

/**
 * Lamanya sejak `nilai`: "12 menit", "3 jam", "2 hari". Dengan `ringkas`: "12 mnt",
 * "3 jam", "2 hari", untuk kolom sempit.
 *
 * Tanpa "lalu": pemakainya yang merangkai kalimatnya ("Terlama 2 hari", "12 menit lalu").
 * Dibulatkan ke bawah, dan paling kecil satu menit: "0 menit" tidak pernah benar untuk
 * sesuatu yang sudah terjadi.
 */
export function lamanya(nilai, locale = 'id-ID', { ringkas = false, kini = Date.now() } = {}) {
  const d = keTanggal(nilai)
  if (!d) return '—'
  const menit = Math.max(1, Math.floor((kini - d.getTime()) / 60000))
  const [n, satuan] = menit < 60 ? [menit, 'minute']
    : menit < 1440 ? [Math.floor(menit / 60), 'hour']
      : [Math.floor(menit / 1440), 'day']
  if (ringkas && locale.startsWith('id')) return `${n} ${SATUAN_RINGKAS_ID[satuan]}`
  return new Intl.NumberFormat(locale, {
    style: 'unit', unit: satuan, unitDisplay: ringkas ? 'short' : 'long',
  }).format(n)
}

/**
 * Jam sebuah pesan di utas percakapan: "09.58" kalau hari ini, "Min 27 Sep 21.40" kalau
 * bukan. Pesan lama butuh harinya; pesan hari ini cukup jamnya.
 */
export function jamPesan(nilai, locale = 'id-ID', kini = new Date()) {
  const d = keTanggal(nilai)
  if (!d) return '—'
  if (hariIni(d, kini)) return jam(d, locale)
  const hari = new Intl.DateTimeFormat(locale, {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: zonaTenant(),
  }).formatToParts(d).filter((b) => b.type !== 'literal').map((b) => b.value).join(' ')
  return `${hari} ${jam(d, locale)}`
}

/** Angka dengan pemisah ribuan sesuai locale. */
export function angka(nilai, locale = 'id-ID') {
  if (nilai === null || nilai === undefined || Number.isNaN(Number(nilai))) return '—'
  return new Intl.NumberFormat(locale).format(Number(nilai))
}

/**
 * Uang dalam USD, dan SELALU USD — bukan mata uang lokal.
 *
 * Biaya model dihitung penyedia dalam dolar. Mengubahnya ke rupiah di layar
 * menuntut kurs, dan kurs yang tidak disebut tanggalnya adalah angka yang salah
 * beberapa minggu kemudian.
 */
export function uangUSD(nilai, locale = 'id-ID', { pecahan = 2 } = {}) {
  if (nilai === null || nilai === undefined || Number.isNaN(Number(nilai))) return '—'
  // `pecahan` di atas 2 untuk biaya satu panggilan: di bawah satu sen ia terbaca "0,00".
  return new Intl.NumberFormat(locale, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: Math.max(2, pecahan),
  }).format(Number(nilai))
}

/**
 * Apakah angka ini ADA — dipakai membedakan "tidak dilaporkan" dari NOL.
 *
 * Bedanya menentukan kalimat yang dibaca klien. Platform yang tidak melaporkan jangkauan
 * menampilkan "—"; yang melaporkan nol menampilkan 0, dan itu berarti "dilihat nol orang".
 * Menyamakan keduanya adalah kabar buruk yang salah — kelas kesalahan yang sama dengan
 * "tanpa pagu" yang ditulis "0% terpakai".
 *
 * NOL, string kosong, dan NaN ditangani terpisah dan sengaja: `0` ADA, `''` tidak, dan
 * `NaN` tidak — `if (!n)` yang naif menganggap ketiganya sama.
 */
export function adaAngka(n) {
  if (n === null || n === undefined || n === '') return false
  return Number.isFinite(Number(n))
}

/**
 * Timestamp ISO -> nilai untuk `<input type="datetime-local">`.
 *
 * Isian itu menuntut `YYYY-MM-DDTHH:mm` dalam waktu SETEMPAT PERAMBAN, tanpa zona.
 * Memberinya string ISO ber-Z membuat isiannya tampil KOSONG — tanpa galat, dan tanpa
 * petunjuk apa pun bahwa nilainya tidak terbaca.
 *
 * ZONA PERAMBAN, bukan zona tenant, dan itu sengaja: `BuatKonten` sudah membaca isian
 * yang sama dengan `new Date(nilai).toISOString()`, yang menafsirkannya sebagai waktu
 * setempat peramban. Memakai zona tenant di sini sementara pembacanya memakai zona
 * peramban akan menggeser jadwalnya sebesar selisih keduanya — kesalahan yang cuma
 * muncul untuk orang yang tidak duduk di WIB, dan karena itu hampir tidak pernah
 * ketahuan.
 */
export function waktuUntukIsian(nilai) {
  if (!nilai) return ''
  const d = new Date(nilai)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
    + `T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** Nilai date/time meeting dalam zona tenant, sesuai jam yang ditampilkan di kartu. */
export function waktuTenantUntukIsian(nilai) {
  if (!nilai) return ''
  const d = new Date(nilai)
  if (Number.isNaN(d.getTime())) return ''
  const bagian = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: zonaTenant(), year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(d).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]))
  return `${bagian.year}-${bagian.month}-${bagian.day}T${bagian.hour}:${bagian.minute}`
}

/** Jam yang diketik untuk tenant -> instant ISO, bukan zona peramban operator. */
export function waktuTenantKeISO(nilai) {
  const [tahun, bulan, hari, jam, menit] = nilai.match(/\d+/g).map(Number)
  const target = Date.UTC(tahun, bulan - 1, hari, jam, menit)
  let saat = target
  // Cari offset zona tenant pada instant itu. Ulang karena offset bisa berganti di
  // sekitar pergantian musim; Jakarta sendiri tidak berganti offset.
  for (let i = 0; i < 3; i++) {
    const terbaca = waktuTenantUntukIsian(new Date(saat))
    const [y, m, d, h, n] = terbaca.match(/\d+/g).map(Number)
    const selisih = target - Date.UTC(y, m - 1, d, h, n)
    if (!selisih) break
    saat += selisih
  }
  if (waktuTenantUntukIsian(new Date(saat)) !== nilai) {
    throw new RangeError('Waktu ini tidak ada dalam zona tenant')
  }
  return new Date(saat).toISOString()
}
